import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Modal,
  Card,
  Typography,
  Tag,
  Input,
} from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  BankOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import SuperAdminAddAdminModal from "../components/SuperAdminAddAdminModal";

const { Title } = Typography;
const { confirm } = Modal;

const SuperAdminCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [addAdminModalVisible, setAddAdminModalVisible] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/superadmin/dashboard");
      const data = response.data?.data || {};
      setCompanies(data.companies || []);
    } catch (error) {
      message.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompany = (company) => {
    confirm({
      title: "Delete Company",
      content: `Are you sure you want to delete "${company.companyName}"? This will deactivate the company (soft delete).`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          apiClient.delete(`/superadmin/companies/${company.companyId}`);
          message.success("Company deleted successfully");
          fetchCompanies();
        } catch (error) {
          message.error("Failed to delete company");
        }
      },
    });
  };

  const handleSuspendCompany = (company) => {
    confirm({
      title: "Suspend Company",
      content: `Are you sure you want to suspend "${company.companyName}"?`,
      okText: "Suspend",
      okType: "warning",
      onOk: async () => {
        try {
          await apiClient.put(
            `/api/superadmin/companies/${company.companyId}/suspend`,
          );
          message.success("Company suspended");
          fetchCompanies();
        } catch (error) {
          message.error("Failed to suspend company");
        }
      },
    });
  };

  const handleReactivateCompany = (company) => {
    confirm({
      title: "Reactivate Company",
      content: `Do you want to reactivate "${company.companyName}"?`,
      okText: "Reactivate",
      onOk: async () => {
        try {
          await apiClient.put(
            `/api/superadmin/companies/${company.companyId}/reactivate`,
          );
          message.success("Company reactivated");
          fetchCompanies();
        } catch (error) {
          message.error("Failed to reactivate company");
        }
      },
    });
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.companyName?.toLowerCase().includes(searchText.toLowerCase()) ||
      company.companyEmail?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const columns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      render: (text) => (
        <Space>
          <BankOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "companyEmail",
      key: "companyEmail",
    },
    {
      title: "Admins",
      dataIndex: "adminCount",
      key: "adminCount",
      render: (count) => <Tag color="blue">{count || 0} Admins</Tag>,
    },
    {
      title: "Total Users",
      dataIndex: "totalUsers",
      key: "totalUsers",
      render: (count) => <Tag color="green">{count || 0} Users</Tag>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const status = (
          record.status || (record.isActive ? "ACTIVE" : "SUSPENDED")
        ).toUpperCase();

        if (status === "DELETED") {
          return <Tag color="red">Deleted</Tag>;
        }

        if (status === "SUSPENDED") {
          return <Tag color="orange">Suspended</Tag>;
        }

        return <Tag color="green">Active</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const status = (
          record.status || (record.isActive ? "ACTIVE" : "SUSPENDED")
        ).toUpperCase();

        return (
          <Space>
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate("/dashboard/superadmin")}
            >
              View
            </Button>

            {status === "ACTIVE" && (
              <Button size="small" onClick={() => handleSuspendCompany(record)}>
                Suspend
              </Button>
            )}

            {status !== "ACTIVE" && (
              <Button
                type="primary"
                size="small"
                onClick={() => handleReactivateCompany(record)}
              >
                Reactivate
              </Button>
            )}

            {status !== "DELETED" && (
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => handleDeleteCompany(record)}
              >
                Delete
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Title level={2}>
          <BankOutlined /> Companies Management
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddAdminModalVisible(true)}
          size="large"
        >
          Add New Company
        </Button>
      </div>

      <Card>
        <Input
          placeholder="Search companies by name or email..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: 16, maxWidth: 400 }}
          size="large"
        />

        <Table
          columns={columns}
          dataSource={filteredCompanies}
          rowKey="companyId"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} companies`,
          }}
        />
      </Card>

      <SuperAdminAddAdminModal
        visible={addAdminModalVisible}
        onCancel={() => setAddAdminModalVisible(false)}
        onSuccess={() => {
          setAddAdminModalVisible(false);
          fetchCompanies();
        }}
      />
    </div>
  );
};

export default SuperAdminCompanies;
